import React, { useState } from 'react';
import classNames from 'classnames';
import styles from './tabs.module.scss';

export interface TabItem {
  key: string;
  label: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  defaultActiveKey?: string;
  activeKey?: string;
  onChange?: (key: string) => void;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  items,
  defaultActiveKey,
  activeKey,
  onChange,
  className
}) => {
  const [internalActiveKey, setInternalActiveKey] = useState<string>(
    defaultActiveKey || (items.length > 0 ? items[0].key : '')
  );

  const currentKey = activeKey !== undefined ? activeKey : internalActiveKey;

  const handleTabClick = (key: string, disabled?: boolean) => {
    if (disabled) return;
    if (activeKey === undefined) {
      setInternalActiveKey(key);
    }
    onChange?.(key);
  };

  const activeContent = items.find(item => item.key === currentKey)?.content;

  return (
    <div className={classNames(styles.tabs, className)}>
      <div className={styles.tabList} role="tablist">
        {items.map(item => (
          <button
            type="button"
            key={item.key}
            role="tab"
            aria-selected={item.key === currentKey}
            aria-disabled={item.disabled}
            className={classNames(styles.tabButton, {
              [styles.isActive]: item.key === currentKey,
              [styles.isDisabled]: item.disabled
            })}
            onClick={() => handleTabClick(item.key, item.disabled)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className={styles.tabPanel} role="tabpanel">
        {activeContent}
      </div>
    </div>
  );
};
